import { SupportHours } from './SupportHours.js';
import {
	ServicesProviderPricing,
	ServicesProviderPricingDelivery,
} from './ServicesProviderPricing.js';
import { Address } from './Address.js';
import { OverflowFees } from './OverflowFees.js';
import { ExternalDataDepos } from './ExternalDataDepos.js';
import { Logos } from './Logos.js';
import { PaymentConfig } from './PaymentConfig.js';
import { FINANCING_BANK_NAME_ENUM } from '@/constants.js';

// TODO fetch relevant services provider function should fetch all relevant ServicesProviders for an order and retun a map from the services being performed to the ServicesProviderId
export interface ServicesProvider {
	// Required fields
	id: string; // 'svp_<unique-id>'
	name: string; // display name
	legalName: string; // name of legal entity
	legalAddress: Address; // address for legal entity involved in sale. county required
	physicalAddress: Address; // address for physical location of services provider
	taxNumber: string; // F-EIN or SSN (tax number) - should this perhaps be more general so individuals could also be sellers?
	payment: PaymentDetails;
	agents: Agent[];
	email: string;
	phone: string;
	website: string;
	overflowFees: OverflowFees[];
	config: ServicesProviderConfig;
	logos: Logos;
	paymentConfig: PaymentConfig;
	support: {
		// required for operations: sales, delivery
		email: string;
		phone: string;
		schedule: { [key: string]: SupportHours };
	};
	externalDataDepos: ExternalDataDepos;
	primaryOemKey?: string; // OEM key of the primary OEM for this services provider — required if using the affiliate portal
	onboardingCompleted: boolean; // indicates if the services provider has completed the onboarding process
	servicesConfigurationEnabled: boolean; // indicates if the services provider has permission to modify their services configuration
	createdAt: Date;
	updatedAt: Date;
}

enum AgentCapacity {
	AGENT = 'AGENT',
	OWNER = 'OWNER',
	ADMIN = 'ADMIN',
}
interface Agent {
	firstName: string;
	middleName: string | null;
	lastName: string;
	email: string;
	capacity: AgentCapacity;
}

export interface BankingDetails {
	accountNumber: string;
	routingNumber: string;
	accountType: string;
	accountClass: string;
}

export interface PaymentDetails {
	stripe?: {
		accountId: string;
		accountLinkUrl: string;
		detailsSubmitted: boolean;
		chargesEnabled: boolean;
		payoutsEnabled: boolean;
	};
	brex?: {
		vendorId: string;
	};
	bank?: BankingDetails;
}

export enum Services {
	SALE = 'sale',
	DELIVERY = 'delivery',
	PICKUP = 'pickup',
	ASSEMBLY = 'assembly',
	INSPECTION = 'inspection',
	TITLING = 'titling',
	REGISTRATION = 'registration',
	FINANCING = 'financing',
	LICENSURE = 'licensure',
	TARIFF = 'tariff',
	INVENTORY = 'inventory',
	PLATFORM = 'platform',
	FREIGHT = 'freight',
	PROCESSING_FEES = 'processing_fees',
	COVERAGE_FULFILLMENT = 'coverage_fulfillment',
	COVERAGE_VENDOR = 'coverage_vendor',
	COVERAGE_PLATFORM = 'coverage_platform',
	COVERAGE_TAX = 'coverage_tax',
	EXTERNAL_TAX = 'external_tax',
	EXTERNAL_FEES = 'external_fees',
	INTERNAL_FEES = 'internal_fees',
}

export interface ServicesProviderConfig {
	// Required if we support the fulfillment service
	[Services.DELIVERY]?: {
		supported: boolean;
		pricing: ServicesProviderPricingDelivery;
	};
	[Services.PICKUP]?: {
		supported: boolean;
		pricing: ServicesProviderPricing;
	};
	[Services.SALE]?: {
		supported: boolean;
		licenseNumber: string; // dealer license number or equivalent
	};
	[Services.ASSEMBLY]?: {
		supported: boolean;
		pricing: ServicesProviderPricing;
	};
	[Services.INSPECTION]?: {
		supported: boolean;
		pricing: ServicesProviderPricing;
	};
	[Services.TITLING]?: {
		supported: boolean;
	};
	[Services.COVERAGE_FULFILLMENT]?: {
		supported: boolean;
		pcrsMcgrawDealerNumber: string;
	};
	[Services.FINANCING]?: {
		supported: boolean;
		lenderConfigs: {
			[lenderId in (typeof FINANCING_BANK_NAME_ENUM)[keyof typeof FINANCING_BANK_NAME_ENUM]]: {
				dealerId?: string;
			};
		};
	};
	// TODO add more services as they become relevant
}

// ... Additional enums and interfaces as needed
